import React, { useEffect, useState, useMemo } from "react";

import {
  makeStyles,
  withStyles,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@material-ui/core";

import { useSnackbar } from "notistack";

import useIcon from "../hooks/useIcon";

import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import Taken from "../assets/images/taken.png";
import Referred from "../assets/images/referrad.png";
import Montly from "../assets/images/montly.png";

import { getInvestors } from "./InvestFunctions";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    borderRadius: theme.spacing(2),
    width: "100%",
    display: "flex",
    flexFlow: "column",
  },
  primaryText: {
    fill: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  contractInfo: {
    display: "flex",
    flexFlow: "column",
    alignItems: "start",
    gap: theme.spacing(2),
    width: "100%",
  },
  contractInfoItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },

  contractInfoItemImageBox: {
    width: "50px",
    minWidth: "50px",
    height: "50px",
  },

  contractInfoItemImage: {
    width: "100%",
    minWidth: "100%",
    objectFit: "cover",
  },
  contractInfoItemInfo: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    margin: "0",
  },
  contractInfoItemInfoInner: {
    display: "flex",
    alignItems: "center",
    padding: "8px 20px",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: "50px",
  },
  coinImage: {
    width: "20px",
    marginLeft: theme.spacing(1),
  },
  copyText: {
    display: "flex",
    cursor: "pointer",
    wordBreak: "break-all",
    gap: theme.spacing(1),

    "&:hover": {
      color: `${theme.palette.primary.main}`,
    },
  },
}));

const MuiAccordion = withStyles((theme) => ({
  root: {
    // border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    borderRadius: theme.spacing(2),
    // "&:not(:last-child)": {
    //   borderBottom: 0,
    // },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      width: "100%",
      margin: "auto",
    },
  },
  expanded: {},
}))(Accordion);

const MuiAccordionSummary = withStyles((theme) => ({
  root: {
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    margin: "0px",
    "&$expanded": {
      margin: "0px",
    },
  },
  expanded: {},
}))(AccordionSummary);

const MuiAccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(AccordionDetails);

function InvestCommissionBox({ network, selectedContract }) {
  const classes = useStyles();
  const [iconImage] = useIcon();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [totalCommisions, setTotalCommisions] = useState(0);
  const [totalReferredInvestments, setTotalReferredInvestments] = useState(0);

  // List view
  const [expanded, setExpanded] = useState("commission-box-accordion");

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const investLink = (wallet) => {
    const { origin } = window.location;

    return `${origin}/invest?referral=${wallet}`;
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);

    enqueueSnackbar("Copy to clipboard", {
      variant: "success",
      autoHideDuration: 1000,
    });
  };

  const convertAmount = (amount, decimals) => {
    return amount / Number(`1e${decimals}`);
  };

  const montlyReceiveComission = useMemo(() => {
    if (totalReferredInvestments) {
      let montlyPercentRate = selectedContract.annualInterestRate / 100 / 12;
      let referralCommissionRate =
        selectedContract.referralCommissionRate / 100;

      let totalMontly = montlyPercentRate * totalReferredInvestments;

      let final = referralCommissionRate * totalMontly;

      return final;
    } else {
      return 0;
    }
  }, [totalReferredInvestments]);

  const getInvestorsData = () => {
    setTotalCommisions(0);
    setTotalReferredInvestments(0);

    getInvestors(network.account, selectedContract.contractAddress).then(
      (res) => {
        if (+res.totalCommissions) {
          setTotalCommisions(
            convertAmount(
              `${res.totalCommissions}`,
              selectedContract.coinIn.decimals
            )
          );
        }

        if (+res.totalReferredInvestments) {
          setTotalReferredInvestments(
            convertAmount(
              `${res.totalReferredInvestments}`,
              selectedContract.coinOut.decimals
            )
          );
        }
      }
    );
  };

  useEffect(() => {
    if (selectedContract.contractAddress) {
      getInvestorsData();
    }
  }, []);

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <MuiAccordion
        square
        expanded={expanded === `commission-box-accordion`}
        onChange={handleChange(`commission-box-accordion`)}
      >
        <MuiAccordionSummary
          aria-controls={`commission-box-accordion`}
          id={`commission-box-accordiond-header`}
        >
          <table className="w-100 commission-list">
            <tbody>
              <tr>
                <td className="name">
                  <Typography variant="h6">Commission Box</Typography>
                </td>
                <td className="referral">
                  <Typography variant="caption" className="lh-1">
                    Referral
                  </Typography>

                  <Typography variant="body1" className="lh-1 fw-bold">
                    {selectedContract.referrerPercent}%
                  </Typography>
                </td>
                <td className="detail">
                  <KeyboardArrowDownIcon className={classes.primaryText} />
                </td>
              </tr>
            </tbody>
          </table>
        </MuiAccordionSummary>
        <MuiAccordionDetails>
          <div className={classes.contractInfo}>
            {selectedContract.name && (
              <>
                <div className={classes.contractInfoItem}>
                  <div className={classes.contractInfoItemImageBox}>
                    <img
                      className={classes.contractInfoItemImage}
                      src={Taken}
                      alt=""
                    />
                  </div>
                  <p className={classes.contractInfoItemInfo}>
                    <Typography variant="body2" className="fw-bold">
                      Commisions <br /> Taken
                    </Typography>
                    <span className={classes.contractInfoItemInfoInner}>
                      {totalCommisions} {selectedContract.coinIn.abbr}
                      <img
                        src={iconImage(selectedContract.coinIn.abbr)}
                        alt={selectedContract.coinIn.abbr}
                        className={classes.coinImage}
                      />
                    </span>
                  </p>
                </div>

                <div className={classes.contractInfoItem}>
                  <div className={classes.contractInfoItemImageBox}>
                    <img
                      className={classes.contractInfoItemImage}
                      src={Referred}
                      alt=""
                    />
                  </div>
                  <p className={classes.contractInfoItemInfo}>
                    <Typography variant="body2" className="fw-bold">
                      Total Deposite <br /> Referrad
                    </Typography>
                    <span className={classes.contractInfoItemInfoInner}>
                      {totalReferredInvestments} {selectedContract.coinIn.abbr}
                      <img
                        src={iconImage(selectedContract.coinIn.abbr)}
                        alt={selectedContract.coinIn.abbr}
                        className={classes.coinImage}
                      />
                    </span>
                  </p>
                </div>

                <div className={classes.contractInfoItem}>
                  <div className={classes.contractInfoItemImageBox}>
                    <img
                      className={classes.contractInfoItemImage}
                      src={Montly}
                      alt=""
                    />
                  </div>
                  <p className={classes.contractInfoItemInfo}>
                    <Typography variant="body2" className="fw-bold">
                      Montly <br /> Commisions
                    </Typography>
                    <span className={classes.contractInfoItemInfoInner}>
                      {montlyReceiveComission} {selectedContract.coinIn.abbr}
                      <img
                        src={iconImage(selectedContract.coinIn.abbr)}
                        alt={selectedContract.coinIn.abbr}
                        className={classes.coinImage}
                      />
                    </span>
                  </p>
                </div>
              </>
            )}

            {network.account && (
              <p className={classes.contractInfoItemInfo}>
                <Typography variant="body2" className="fw-bold">
                  Referral Link
                </Typography>
                <span
                  className={classes.copyText}
                  onClick={() => copy(investLink(network.account))}
                >
                  {investLink(network.account)}

                  <FileCopyIcon />
                </span>
              </p>
            )}
          </div>
        </MuiAccordionDetails>
      </MuiAccordion>
    </Paper>
  );
}

export default InvestCommissionBox;
